import * as React from 'react';
import { Loader2, X } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'controller/types';
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import { warning } from 'core/components/Notify';
import { useCommunicationSetupSlice } from 'features/CommunicationSetup/slice';
import { SOCIAL_MEDIA } from 'features/SocialHub/components/DesktopComponents/ListDetails/SocialHubDashboard/constants';
import { useSocialMediaSelection } from 'features/SocialHub/components/DesktopComponents/ListDetails/SocialHubDashboard/hooks/useSocialMediaSelection';
import { useSocialHubStatus } from 'features/SocialHub/components/DesktopComponents/ListDetails/SocialHubDashboard/hooks/useSocialHubStatus';
import { useSocialAuth } from 'features/SocialHub/components/DesktopComponents/ListDetails/SocialHubDashboard/hooks/useSocialAuth';
import { useSocialHubEvents } from 'features/SocialHub/components/DesktopComponents/ListDetails/SocialHubDashboard/hooks/useSocialHubEvents';
import { AccountSelectionList } from 'features/SocialHub/components/DesktopComponents/ListDetails/SocialHubDashboard/components/AccountSelectionList';
import { Modal } from './ui/Modal';
import { Toggle } from './ui/Toggle';
import { DMButton, DMDiv, DMText } from './Primitives';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const platforms = [
  { name: 'Facebook', id: 'fb' },
  { name: 'Instagram', id: 'ig' },
  { name: 'Google Business', id: 'gb' },
  { name: 'WhatsApp', id: 'wa' },
  { name: 'YouTube', id: 'yt' },
];

export const SocialMediaManagementModal = ({ isOpen, onClose }: Props) => {
  const dispatch = useDispatch();
  const authData = useAuth();
  const { actions } = useCommunicationSetupSlice();
  const state: any = useSelector((root: RootState) => root.communicationSetup);
  const [triggerConnect, setTriggerConnect] = React.useState(false);
  const { selectedSocialMedia, selectSocialMedia } = useSocialMediaSelection();
  const { socialHubStatus, loader } = useSocialHubStatus(state?.isUpsertLoading);
  const { connect, whatsAppSession } = useSocialAuth(selectedSocialMedia);
  useSocialHubEvents();

  React.useEffect(() => {
    if (!triggerConnect || !selectedSocialMedia) return;
    setTriggerConnect(false);
    connect();
  }, [connect, selectedSocialMedia, triggerConnect]);

  const isWhatsApp = selectedSocialMedia?.NAME === 'WhatsApp Business';
  const isWhatsAppConnected =
    isWhatsApp && socialHubStatus?.[selectedSocialMedia?.id]?.status;
  const listData =
    state?.listResponse?.list || state?.listResponse?.data || state?.listResponse;
  const hasList = Boolean(state?.listResponse?.length || state?.listResponse?.list?.length);
  const hasToken = Boolean(
    state?.ACCESS_TOKEN || state?.listResponse?.access_token || isWhatsApp
  );
  const showAccountSelection = hasList && hasToken && !isWhatsAppConnected;
  const isProcessing = triggerConnect || state?.isUpsertLoading;

  const handleToggle = (platformId: string, currentStatus: boolean) => {
    if (currentStatus) {
      warning(
        'Please create a support ticket to disconnect this profile.',
        true
      );
      return;
    }
    const platform = SOCIAL_MEDIA.find(item => item.id === platformId);
    if (platform) {
      selectSocialMedia(platform);
      setTriggerConnect(true);
    }
  };

  const handleSelection = (item: any) => {
    if (selectedSocialMedia?.NAME === 'WhatsApp Business') {
      dispatch(
        actions.createCommunicationSetupStart({
          URL: 'whatsapp/selectBusiness',
          data: {
            INSTITUTE_ID: authData?.INSTITUTE_ID || 0,
            BRANCH_ID: authData?.BRANCH_ID || 0,
            WA_PH_ID: whatsAppSession?.phoneNumberId || '',
            BUSINESS_ID: whatsAppSession?.wabaId || '',
            CODE: whatsAppSession?.code || '',
          },
          message: 'Selected Successfully !',
        })
      );
    } else if (
      ['Google Business', 'Youtube'].includes(selectedSocialMedia?.NAME)
    ) {
      dispatch(
        actions.createCommunicationSetupStart({
          URL: 'google/update_info',
          data: {
            BRANCH_ID: authData?.BRANCH_ID,
            ACCESS_TOKEN: state?.listResponse?.access_token,
            REFRESH_TOKEN: state?.listResponse?.refresh_token,
            PREFERRED_ID: item?.id || item?.name,
            SERVICE: selectedSocialMedia?.id,
          },
          message: 'Selected Successfully !',
        })
      );
    } else {
      dispatch(
        actions.createCommunicationSetupStart({
          URL: 'facebook/update_info',
          data: {
            BRANCH_ID: authData?.BRANCH_ID,
            ACCESS_TOKEN: item?.access_token || state?.ACCESS_TOKEN,
            PAGE_ID: item?.id || item?.fb_page_id,
            SERVICE: selectedSocialMedia?.id,
          },
          message: 'Selected Successfully !',
        })
      );
    }
    dispatch(actions.clearSocialHubData());
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} className="z-[9000]">
        <DMDiv
          role="dialog"
          aria-modal="true"
          aria-labelledby="social-media-management-title"
          className="text-left"
        >
          <DMDiv className="mb-[2vh] flex items-start justify-between gap-[2vh]">
            <DMDiv>
              <DMText
                as="h2"
                id="social-media-management-title"
                className="text-[2.5vh] font-bold text-gray-900"
              >
                Social Media Management
              </DMText>
              <DMText className="mt-[0.5vh] text-[1.5vh] text-gray-500">
                Connect and manage your institution&apos;s social profiles.
              </DMText>
            </DMDiv>
            <DMButton
              type="button"
              onClick={onClose}
              aria-label="Close social media management"
              className="rounded-full p-[0.8vh] text-gray-500 hover:bg-gray-100"
            >
              <X size="2.2vh" />
            </DMButton>
          </DMDiv>

          {isProcessing && (
            <DMDiv className="mb-[1.5vh] flex items-center gap-[0.8vh] rounded-[1vh] bg-primary-10 px-[1.2vh] py-[1vh] text-primary-main">
              <Loader2 className="animate-spin" size="1.8vh" />
              <DMText className="text-[1.4vh] font-semibold">Connecting…</DMText>
            </DMDiv>
          )}

          <DMDiv className="flex flex-col gap-[1vh]">
            {platforms.map(platform => {
              const connected = Boolean(socialHubStatus?.[platform.id]?.status);
              const socialConfig = SOCIAL_MEDIA.find(item => item.id === platform.id);
              const icon = socialConfig?.activeIcon as React.ReactElement;
              const accountName =
                socialHubStatus?.[platform.id]?.info?.name || platform.name;

              return (
                <DMDiv
                  key={platform.id}
                  className="flex items-center justify-between gap-[1.5vh] rounded-[1.2vh] border border-gray-200 px-[1.4vh] py-[1.1vh]"
                >
                  <DMDiv className="flex min-w-0 items-center gap-[1vh]">
                    {icon ? React.cloneElement(icon, { className: 'text-[3vh]' }) : null}
                    <DMDiv className="min-w-0">
                      <DMText className="truncate text-[1.6vh] font-bold text-gray-900">
                        {accountName}
                      </DMText>
                      <DMText className={`text-[1.3vh] ${connected ? 'text-green-600' : 'text-gray-400'}`}>
                        {connected ? 'Connected' : 'Not connected'}
                      </DMText>
                    </DMDiv>
                  </DMDiv>
                  <Toggle
                    checked={connected}
                    onChange={() => handleToggle(platform.id, connected)}
                    disabled={loader || isProcessing}
                  />
                </DMDiv>
              );
            })}
          </DMDiv>
        </DMDiv>
      </Modal>

      <Modal
        isOpen={showAccountSelection}
        onClose={() => dispatch(actions.clearSocialHubData())}
        className="z-[10000]"
      >
        <AccountSelectionList
          list={listData}
          selectedSocialMedia={selectedSocialMedia}
          onSelect={handleSelection}
          onCancel={() => dispatch(actions.clearSocialHubData())}
        />
      </Modal>
    </>
  );
};
